import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BarChart3, Copy, ExternalLink, Pencil, Plus, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Skeleton } from "../ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { errorMessage, pagesApi } from "../../lib/api";
import { copyToClipboard } from "../../lib/clipboard";
import { activePixelPlatforms } from "../../lib/tracking";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

const Th = ({ children, className = "" }) => <TableHead className={`text-[10px] uppercase tracking-[0.2em] ${className}`}>{children}</TableHead>;

const RowActions = ({ page, onDuplicate, onDelete }) => (
  <div className="flex items-center justify-end gap-1">
    <Button asChild size="icon" variant="ghost" aria-label="Edit" title="Edit" data-testid={`edit-${page.slug}`}>
      <Link to={`/admin/pages/${page.id}`}>
        <Pencil className="h-4 w-4" />
      </Link>
    </Button>
    <Button asChild size="icon" variant="ghost" aria-label="Stats" title="Stats" data-testid={`stats-${page.slug}`}>
      <Link to={`/admin/pages/${page.id}/stats`}>
        <BarChart3 className="h-4 w-4" />
      </Link>
    </Button>
    <Button size="icon" variant="ghost" aria-label="Copy link" title="Copy link" onClick={() => copyToClipboard(`${window.location.origin}/lp/${page.slug}`, "Link copied", `${window.location.origin}/lp/${page.slug}`)} data-testid={`copy-${page.slug}`}>
      <Copy className="h-4 w-4" />
    </Button>
    {page.status === "published" && (
      <Button asChild size="icon" variant="ghost" aria-label="Open live page" title="Open live page">
        <a href={`/lp/${page.slug}`} target="_blank" rel="noreferrer" data-testid={`open-${page.slug}`}>
          <ExternalLink className="h-4 w-4" />
        </a>
      </Button>
    )}
    <Button size="icon" variant="ghost" aria-label="Duplicate" title="Duplicate" onClick={() => onDuplicate(page.id)} data-testid={`duplicate-${page.slug}`}>
      <FileText className="h-4 w-4" />
    </Button>
    <Button size="icon" variant="ghost" aria-label="Delete" title="Delete" className="hover:text-destructive" onClick={() => onDelete(page)} data-testid={`delete-${page.slug}`}>
      <Trash2 className="h-4 w-4" />
    </Button>
  </div>
);

/**
 * Landing pages table with row actions (edit / stats / copy / open / duplicate / delete).
 * `pages` null = loading. `onChanged` is called after a delete so the parent can refresh.
 */
const PagesTable = ({ pages, onChanged, emptyTitle = "No landing pages yet" }) => {
  const navigate = useNavigate();
  const [toDelete, setToDelete] = useState(null);

  const duplicate = async (id) => {
    try {
      const p = await pagesApi.duplicate(id);
      toast.success("Page duplicated");
      navigate(`/admin/pages/${p.id}`);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await pagesApi.remove(toDelete.id);
      toast.success(`Deleted “${toDelete.title}”`);
      setToDelete(null);
      onChanged?.();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <div className="border border-border bg-card">
      {pages === null ? (
        <div className="space-y-3 p-5">
          {["a", "b", "c"].map((k) => (
            <Skeleton key={k} className="h-12" />
          ))}
        </div>
      ) : pages.length === 0 ? (
        <div className="flex flex-col items-center justify-center px-6 py-20 text-center" data-testid="pages-empty">
          <p className="display text-4xl uppercase">{emptyTitle}</p>
          <p className="mt-2 max-w-sm text-sm text-muted-foreground">Create your first promo page, add your pixels, and share the link in your ads.</p>
          <Button asChild className="mt-6 h-11 rounded-none px-6 text-[11px] font-semibold uppercase tracking-[0.2em]">
            <Link to="/admin/pages/new">
              <Plus className="mr-2 h-4 w-4" /> Create a page
            </Link>
          </Button>
        </div>
      ) : (
        <Table data-testid="pages-table">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <Th>Page</Th>
              <Th>Status</Th>
              <Th className="text-right">Views</Th>
              <Th className="text-right">Unique</Th>
              <Th className="text-right">Clicks</Th>
              <Th className="hidden md:table-cell">Pixels</Th>
              <Th className="hidden lg:table-cell">Updated</Th>
              <Th className="text-right">Actions</Th>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pages.map((p) => (
              <TableRow key={p.id} data-testid={`page-row-${p.slug}`}>
                <TableCell>
                  <Link to={`/admin/pages/${p.id}`} className="font-semibold hover:text-primary">
                    {p.title}
                  </Link>
                  <p className="mt-0.5 text-xs text-muted-foreground">/lp/{p.slug}</p>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={p.status === "published" ? "rounded-none border-primary/50 text-primary" : "rounded-none border-border text-muted-foreground"}>
                    {p.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right tabular-nums">{p.stats?.views ?? 0}</TableCell>
                <TableCell className="text-right tabular-nums">{p.stats?.unique_visitors ?? 0}</TableCell>
                <TableCell className="text-right tabular-nums">{p.stats?.clicks ?? 0}</TableCell>
                <TableCell className="hidden md:table-cell">
                  {activePixelPlatforms(p.pixel).length ? (
                    <span className="text-xs text-primary">{activePixelPlatforms(p.pixel).join(" · ")}</span>
                  ) : (
                    <span className="text-xs text-muted-foreground">Off</span>
                  )}
                </TableCell>
                <TableCell className="hidden text-sm text-muted-foreground lg:table-cell">{fmtDate(p.updated_at)}</TableCell>
                <TableCell>
                  <RowActions page={p} onDuplicate={duplicate} onDelete={setToDelete} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="rounded-none border-border bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle className="display text-3xl uppercase tracking-wide">Delete this page?</AlertDialogTitle>
            <AlertDialogDescription>“{toDelete?.title}” and all of its tracking data will be permanently removed. This can’t be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="rounded-none bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="delete-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PagesTable;
